import React, { useState, useEffect } from "react";
import { Plus, X, Trash2, Images, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { axiosAuth } from "../shared/axiosAuth";
import { API } from "../data/constants";

export const MomentsPage = ({ moments, fetchMoments, pets, categories }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: "", photo: "", description: "", pet_id: "", category_id: "" });
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    if (!formData.title.trim()) { toast.error("Please enter a title"); return; } 
    setLoading(true); 
    try { 
      await axiosAuth.post(`${API}/moments`, formData); 
      toast.success("Moment captured! 📸"); 
      await fetchMoments(); 
      setFormData({ title: "", photo: "", description: "", pet_id: "", category_id: "" }); 
      setShowForm(false); 
    } catch (error) { 
      toast.error("Failed to save"); 
    } finally { 
      setLoading(false); 
    } 
  };

  const handleDelete = async (id) => { 
    if (!window.confirm("Delete this moment?")) return; 
    try { 
      await axiosAuth.delete(`${API}/moments/${id}`); 
      toast.success("Deleted"); 
      await fetchMoments(); 
    } catch (error) { 
      toast.error("Failed"); 
    } 
  };

  const handleImageUpload = (e) => { 
    const file = e.target.files[0]; 
    if (file && file.size <= 5 * 1024 * 1024) { 
      const reader = new FileReader(); 
      reader.onloadend = () => setFormData({ ...formData, photo: reader.result }); 
      reader.readAsDataURL(file); 
    } 
  };

  const sortedMoments = [...moments].sort((a, b) => 
    sortBy === "newest" 
      ? new Date(b.created_at) - new Date(a.created_at) 
      : new Date(a.created_at) - new Date(b.created_at)
  );
  const momentsWithPhotos = sortedMoments.filter(m => m.photo);

  // Slideshow navigation
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % momentsWithPhotos.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + momentsWithPhotos.length) % momentsWithPhotos.length);

  // Auto-advance slideshow
  useEffect(() => {
    if (showSlideshow && momentsWithPhotos.length > 1) {
      const timer = setInterval(nextSlide, 5000);
      return () => clearInterval(timer);
    }
  }, [showSlideshow, momentsWithPhotos.length]);

  // Slideshow Modal
  if (showSlideshow && momentsWithPhotos.length > 0) {
    const moment = momentsWithPhotos[currentSlide];
    const pet = pets.find(p => p.id === moment.pet_id);
    return (
      <div className="fixed inset-0 bg-charcoal z-50 flex flex-col">
        {/* Close button */}
        <div className="absolute top-4 right-4 z-10">
          <button onClick={() => setShowSlideshow(false)} className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all">
            <X size={24} />
          </button>
        </div>
        
        {/* Main image */}
        <div className="flex-1 flex items-center justify-center p-8 relative">
          <button onClick={prevSlide} className="absolute left-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all">
            <ChevronLeft size={32} />
          </button>
          
          <div className="max-w-4xl w-full">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img src={moment.photo} alt={moment.title} className="w-full max-h-[70vh] object-contain bg-black" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
                <h2 className="font-heading text-3xl font-bold text-white mb-2">{moment.title}</h2>
                {pet && <p className="text-white/80 text-lg mb-2">🐾 {pet.name}</p>}
                {moment.description && <p className="text-white/70">{moment.description}</p>}
                <p className="text-white/50 text-sm mt-2">{new Date(moment.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
            </div>
          </div>
          
          <button onClick={nextSlide} className="absolute right-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all">
            <ChevronRight size={32} />
          </button>
        </div>
        
        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-8">
          {momentsWithPhotos.map((_, idx) => (
            <button 
              key={idx} 
              onClick={() => setCurrentSlide(idx)}
              className={`w-3 h-3 rounded-full transition-all ${idx === currentSlide ? 'bg-white w-8' : 'bg-white/40'}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="moments-page" className="fade-in relative z-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-charcoal">Precious Moments 📸</h1>
        <div className="flex items-center gap-3">
          {momentsWithPhotos.length > 0 && (
            <button 
              onClick={() => { setCurrentSlide(0); setShowSlideshow(true); }}
              className="h-12 px-5 border-2 border-purple-300 text-purple-600 rounded-full font-medium hover:bg-purple-50 transition-all flex items-center gap-2"
            >
              <Images size={18} />
              Slideshow
            </button>
          )}
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-12 px-4 border-2 border-gray-200 rounded-full bg-white focus:border-sage outline-none">
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
          <button data-testid="add-moment-button" onClick={() => setShowForm(true)} className="bg-sandy text-white px-6 py-3 rounded-full font-medium hover:brightness-90 transition-all flex items-center gap-2">
            <Plus size={20} />Add Moment
          </button>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-semibold text-charcoal">Capture a Moment 📸</h2>
              <button onClick={() => setShowForm(false)} className="text-stone hover:text-charcoal p-2"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-charcoal font-medium mb-2">Title *</label>
                <input data-testid="moment-title-input" type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none" required />
              </div>
              <div>
                <label className="block text-charcoal font-medium mb-2">Photo 📷</label>
                <div className="flex items-center gap-4">
                  {formData.photo ? (
                    <div className="relative">
                      <img src={formData.photo} alt="" className="w-20 h-20 rounded-xl object-cover" />
                      <button type="button" onClick={() => setFormData({ ...formData, photo: "" })} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"><X size={14} /></button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center text-3xl">📷</div>
                  )}
                  <label className="cursor-pointer bg-paper text-sage border-2 border-sage/20 hover:border-sage px-4 py-2 rounded-full transition-all">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />Choose Photo
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-charcoal font-medium mb-2">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full h-24 border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-2 outline-none resize-none" />
              </div>
              {pets.length > 0 && (
                <div>
                  <label className="block text-charcoal font-medium mb-2">Pet 🐾</label>
                  <select value={formData.pet_id} onChange={(e) => setFormData({ ...formData, pet_id: e.target.value })} className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none">
                    <option value="">Select pet</option>
                    {pets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              <button data-testid="submit-moment-button" type="submit" disabled={loading} className="w-full bg-sandy text-white py-4 rounded-full font-medium hover:brightness-90 transition-all disabled:opacity-50">
                {loading ? "Saving..." : "Save Moment 📸"}
              </button>
            </form>
          </div>
        </div>
      )}

      {sortedMoments.length === 0 ? (
        <div className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-3xl border border-white/50">
          <span className="text-5xl block mb-4">📸</span>
          <h3 className="font-heading text-xl text-charcoal mb-2">No moments yet</h3>
          <button onClick={() => setShowForm(true)} className="bg-sandy text-white px-8 py-4 rounded-full text-lg font-medium hover:brightness-90 transition-all mt-4">
            Capture Your First Moment 📷
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedMoments.map((m) => (
            <div key={m.id} className="bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-white/50">
              <div className="h-48 bg-gradient-to-br from-sandy/20 to-terracotta/20">
                {m.photo ? <img src={m.photo} alt={m.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-5xl">📷</div>}
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-semibold text-charcoal mb-2">{m.title}</h3>
                {m.description && <p className="text-stone text-sm line-clamp-2 mb-3">{m.description}</p>}
                <button onClick={() => handleDelete(m.id)} className="text-red-500 text-sm flex items-center gap-1 hover:text-red-600">
                  <Trash2 size={14} />Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MomentsPage;
